import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Zap, Loader2, Plus, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export const CreditsCard = () => {
  const [data, setData] = useState(null);
  const [packs, setPacks] = useState([]);
  const [open, setOpen] = useState(false);
  const [buying, setBuying] = useState(null);
  const polled = useRef(false);

  const load = () => api.get("/credits/me").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
    api.get("/credits/packs").then((r) => setPacks(r.data)).catch(() => {});
  }, []);

  // Handle return from Stripe top-up checkout (?topup=session_id)
  useEffect(() => {
    if (polled.current) return;
    const sid = new URLSearchParams(window.location.search).get("topup");
    if (!sid) return;
    polled.current = true;
    window.history.replaceState({}, "", window.location.pathname);
    let tries = 0;
    const poll = async () => {
      tries += 1;
      if (tries > 8) return;
      try {
        const res = await api.get(`/credits/status/${sid}`);
        if (res.data.payment_status === "paid") {
          const mins = res.data.activate_minutes;
          toast.success(mins > 0
            ? `Payment received! Your ${res.data.credits} credits will appear within about ${mins} minutes.`
            : `Payment received! Your ${res.data.credits} credits have been added.`);
          load();
          return;
        }
      } catch {}
      setTimeout(poll, 2500);
    };
    poll();
  }, []);

  const buy = async (packId) => {
    setBuying(packId);
    try {
      const res = await api.post("/credits/checkout", { pack_id: packId, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBuying(null);
    }
  };

  if (!data || data.free) return null;

  const pct = data.pct_used || 0;
  const exhausted = data.exhausted;
  const low = data.low;
  const barColor = exhausted ? "#ef4444" : low ? "#eab308" : "#f97316";
  const period = data.period === "trial" ? "Free trial" : "This month";

  return (
    <>
      <div className={`rounded-sm p-6 border ${exhausted ? "border-red-500/40 bg-red-500/5" : low ? "border-yellow-500/40 bg-yellow-500/5" : "border-white/10 bg-[#18181b]"}`} data-testid="credits-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-sm bg-primary/15 flex items-center justify-center"><Zap className="h-5 w-5 text-primary" /></div>
            <div>
              <h3 className="font-display text-xl tracking-wide">AI USAGE · {period.toUpperCase()}</h3>
              <p className="text-xs text-muted-foreground">Ava's monthly allowance — resets each billing month.</p>
            </div>
          </div>
          <Button data-testid="buy-credits-btn" onClick={() => setOpen(true)} className="rounded-sm bg-primary hover:bg-orange-600 text-white font-semibold gap-1.5 h-9">
            <Plus className="h-4 w-4" /> Buy credits
          </Button>
        </div>

        <div className="flex items-end justify-between mb-2">
          <div>
            <p className="font-display text-4xl tracking-tight" data-testid="credits-remaining">{Math.round(data.remaining)}</p>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">credits remaining of {data.allowance}</p>
          </div>
          {data.pending_credits > 0 && (
            <span className="text-xs text-muted-foreground">+{data.pending_credits} arriving soon</span>
          )}
        </div>

        <div className="h-2.5 w-full rounded-sm bg-white/10 overflow-hidden">
          <div className="h-full rounded-sm transition-[width] duration-500" style={{ width: `${pct}%`, backgroundColor: barColor }} />
        </div>

        {exhausted && (
          <p className="flex items-center gap-2 text-sm text-red-400 mt-3" data-testid="credits-exhausted-note">
            <AlertTriangle className="h-4 w-4" /> Ava has paused answering new customers. Buy more credits to resume instantly.
          </p>
        )}
        {!exhausted && low && (
          <p className="flex items-center gap-2 text-sm text-yellow-400 mt-3" data-testid="credits-low-note">
            <AlertTriangle className="h-4 w-4" /> Running low — top up so Ava keeps answering.
          </p>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#18181b] border-white/10 text-foreground" data-testid="buy-credits-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-wide">BUY MORE CREDITS</DialogTitle>
            <DialogDescription className="text-muted-foreground">Top up Ava's allowance for this billing month. Credits are added as soon as your payment clears, and don't roll over.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {packs.map((p) => (
              <div key={p.id} className="flex items-center justify-between bg-[#0f0f11] border border-white/10 rounded-sm p-4" data-testid={`pack-${p.id}`}>
                <div>
                  <p className="font-display text-2xl tracking-tight">{p.credits} credits</p>
                  <p className="text-xs text-muted-foreground">One-off top-up · this month only</p>
                </div>
                <Button data-testid={`buy-${p.id}`} onClick={() => buy(p.id)} disabled={!!buying} className="rounded-sm bg-primary hover:bg-orange-600 text-white font-semibold h-10 px-5 min-w-[90px]">
                  {buying === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : `£${p.price}`}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground text-center mt-2">Secure payment via Stripe.</p>
        </DialogContent>
      </Dialog>
    </>
  );
};
